import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Typewriter } from "react-simple-typewriter";
import {
  FaPhone,
  FaEnvelope,
  FaBars,
  FaTimes,
  FaSun,
  FaMoon,
  FaChevronDown,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import EnquiryPopup from "../components/EnquiryPopup";

const Navbar = () => {
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showMobileDestinations, setShowMobileDestinations] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === "dark" ? "light" : "dark");
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > lastScrollY ? "down" : "up";
      setIsHidden(scrollDirection === "down" && currentScrollY > 100);
      if (currentScrollY === 0) setIsHidden(false);
      setIsScrolled(currentScrollY > 50);
      setLastScrollY(currentScrollY);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const destinations = {
    international: [
      "Dubai", "Thailand", "Sri Lanka", "Hong Kong",
      "Maldives", "Singapore", "Bali", "Malaysia", "Vietnam",
    ],
    national: ["Kerala", "Goa", "Himachal", "Kashmir"],
  };

  const destinationQueryMap = {
    Dubai: { destination: null, country: "UAE" },
    Thailand: { destination: null, country: "Thailand" },
    "Sri Lanka": { destination: null, country: "Sri Lanka" },
    "Hong Kong": { destination: null, country: "Hong Kong" },
    Maldives: { destination: "Maldives", country: "Maldives" },
    Singapore: { destination: "Singapore", country: "Singapore" },
    Bali: { destination: "Bali", country: "Indonesia" },
    Kerala: { destination: "Kerala", country: "India" },
    Goa: { destination: "Goa", country: "India" },
  };

  const handleDestinationClick = (label) => {
    const match = destinationQueryMap[label];
    console.log("match",match)
    if (match) {
      const { destination, country } = match;
      const queryParams = new URLSearchParams();
      if (destination) queryParams.set("destination", destination);
      if (country) queryParams.set("country", country);
      navigate(`/all_packages?${queryParams.toString()}`);
      setShowDropdown(false);
      setIsMobileMenuOpen(false);
    } else {
      setShowPopup(true);
      setShowDropdown(false);
    }
  };

  return (
    <motion.header
      className="fixed w-full top-0 z-50"
      animate={isHidden ? "hidden" : "visible"}
      variants={{ visible: { y: 0 }, hidden: { y: "-100%" } }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      ref={mobileMenuRef}
    >
      <div className="bg-gradient-to-r from-gray-900 to-black text-gray-300 text-sm py-2 px-4 border-b border-gray-800">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center">
          <div className="w-full flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
            {/* Typewriter Text */}
            <div className="text-center sm:text-left text-[0.7rem] sm:text-sm text-white w-full sm:w-auto">
              <Typewriter
                words={[
                  "Unmatched Access to Luxury Retreats",
                  "Elite Rewards & VIP Experiences",
                ]}
                loop={0}
                cursor
                cursorStyle="|"
                typeSpeed={50}
                deleteSpeed={30}
                delaySpeed={2500}
              />
            </div>

            {/* Email + Phone (icons only on desktop) */}
            <div className="flex items-center gap-3 text-white text-[0.65rem] sm:text-xs whitespace-nowrap overflow-x-auto">
              <span className="flex items-center gap-1">
                <FaEnvelope className="text-blue-400 hidden sm:inline-block" />
                prince@tripocationholiday.com
              </span>
              <span className="flex items-center gap-1">
                <FaPhone className="text-blue-400 hidden sm:inline-block" />
                +91 92201 98202
              </span>
            </div>
          </div>
        </div>
      </div>

      <nav className={`bg-gray-900 dark:bg-black transition-shadow duration-300 ${isScrolled ? "shadow-xl" : ""}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <img
                src="/tripocation.jpg"
                alt="Logo"
                className="w-12 h-12 md:w-16 md:h-16 object-contain"
              />
              <div className="ml-2 sm:ml-3">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-white">
                  Tripocation Holiday Pvt. Ltd.
                </h1>
                <p className="text-[10px] sm:text-xs text-gray-400 text-end">
                  Your Travel Story Start's Here
                </p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                Home
              </Link>

              {/* Destinations Dropdown */}
              <div
                className="relative"
                onMouseEnter={() => setShowDropdown(true)}
                onMouseLeave={() => {
                  setTimeout(() => {
                    if (!dropdownRef.current?.matches(":hover")) {
                      setShowDropdown(false);
                    }
                  }, 100);
                }}
                ref={dropdownRef}
              >
                <button className="flex items-center text-gray-300 hover:text-white font-medium text-sm">
                  Destinations
                  <FaChevronDown className="ml-2 text-xs mt-1" />
                </button>
                <AnimatePresence>
                  {showDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute top-full left-0 mt-2 bg-gray-800 shadow-2xl rounded-lg p-4 border border-gray-700 z-50 w-[420px]"
                    >
                      <div className="flex justify-between gap-6 text-sm">
                        <div className="flex-1">
                          <h3 className="text-xs font-semibold text-blue-400 mb-2 uppercase">International</h3>
                          {destinations.international.map((item) => (
                            <span
                              key={item}
                              onClick={() => handleDestinationClick(item)}
                              className="block py-1 px-2 text-gray-300 rounded-md cursor-pointer hover:bg-gray-700"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xs font-semibold text-blue-400 mb-2 uppercase">Domestic</h3>
                          {destinations.national.map((item) => (
                            <span
                              key={item}
                              onClick={() => handleDestinationClick(item)}
                              className="block py-1 px-2 text-gray-300 rounded-md cursor-pointer hover:bg-gray-700"
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <Link to="/enquiry-page" className="text-gray-300 hover:text-white transition-colors">
                Enquiry
              </Link>
              <Link to="/about-us" className="text-gray-300 hover:text-white transition-colors">
                About
              </Link>
              <Link to="/pay" className="text-gray-300 hover:text-white transition-colors">
                Payment
              </Link>
              <Link to="/contact-us" className="text-gray-300 hover:text-white transition-colors">
                Contact us
              </Link>

              {/* Theme Toggle */}
              <div className="flex items-center space-x-2">
                <FaSun className={`text-yellow-400 ${theme === "dark" ? "opacity-50" : "opacity-100"}`} />
                <div
                  onClick={toggleTheme}
                  className="w-12 h-6 flex items-center bg-gray-500 rounded-full p-1 cursor-pointer"
                >
                  <div
                    className={`bg-white w-4 h-4 rounded-full transform transition-transform duration-300 ${theme === "dark" ? "translate-x-6" : ""}`}
                  ></div>
                </div>
                <FaMoon className={`text-blue-300 ${theme === "light" ? "opacity-50" : "opacity-100"}`} />
              </div>
            </div>

            {/* Mobile Menu Toggle */}
            <button
              className="md:hidden p-2 text-gray-400 hover:text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-gray-800 text-gray-200 px-4 pt-2 pb-4 space-y-2"
          >
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 border-b border-gray-700">
              Home
            </Link>

            <button
              onClick={() => setShowMobileDestinations(!showMobileDestinations)}
              className="w-full text-left py-2 border-b border-gray-700 flex items-center justify-between"
            >
              Destinations
              <FaChevronDown
                className={`transition-transform ${showMobileDestinations ? "rotate-180" : "rotate-0"}`}
              />
            </button>

            <AnimatePresence>
              {showMobileDestinations && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="pt-2 px-1"
                >
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex-1 min-w-[45%]">
                      <h3 className="text-xs font-semibold text-blue-400 mb-2 uppercase">International</h3>
                      {destinations.international.map((item) => (
                        <div
                          key={item}
                          onClick={() => handleDestinationClick(item)}
                          className="text-sm text-gray-300 cursor-pointer hover:text-white py-1"
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 min-w-[45%]">
                      <h3 className="text-xs font-semibold text-blue-400 mb-2 uppercase">Domestic</h3>
                      {destinations.national.map((item) => (
                        <div
                          key={item}
                          onClick={() => handleDestinationClick(item)}
                          className="text-sm text-gray-300 cursor-pointer hover:text-white py-1"
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <Link to="/enquiry-page" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 border-b border-gray-700">
              Enquiry
            </Link>
            <Link to="/about-us" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 border-b border-gray-700">
              About
            </Link>
            <Link to="/pay" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 border-b border-gray-700">
              Payment
            </Link>
            <Link to="/contact-us" onClick={() => setIsMobileMenuOpen(false)} className="block py-2 border-b border-gray-700">
              Contact us
            </Link>

            {/* Theme toggle */}
            <div
              onClick={toggleTheme}
              className="w-12 h-6 flex items-center bg-gray-500 rounded-full p-1 cursor-pointer mt-2"
            >
              <div
                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${theme === "dark" ? "translate-x-6" : ""}`}
              ></div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Enquiry Popup */}
      <EnquiryPopup isOpen={showPopup} onClose={() => setShowPopup(false)} />
    </motion.header>
  );
};

export default Navbar;
